import {
  Checkbox,
  IconWarning,
  RowCell,
  TableRow,
  Text,
} from '@bbl-digital/snorre'
import {
  getFormattedPhone,
  useIsMobile,
  IconStop,
} from '@bbl-digital/snorre-v2'
import { FC } from 'react'
import { useAppSelector } from 'shared/store/store'
import {
  IRecipientModel,
  FilterTypeEnum,
} from 'Modules/Communication/shared/models/Recipients'
import { useTheme } from '@emotion/react'
import { getMessageRecipients } from 'shared/store/sendMessage/recipients/selectors'
import ReactTooltip from 'react-tooltip'
import { Ul, Li, IconWrapper } from './styles'
import useRecipientsChecked from 'Modules/Communication/shared/hooks/useRecipientsChecked'
import { getBblDisplayNames } from 'shared/store/bbl'

interface IProps {
  recipient: IRecipientModel
}

const RecipientListItem: FC<IProps> = ({ recipient }) => {
  const { bgLight, alert } = useTheme()
  const isMobile = useIsMobile()

  const { openState, filters } = useAppSelector(getMessageRecipients)
  const { myPageShort } = useAppSelector(getBblDisplayNames)

  const { handleSingleCheckboxChange, recipientChecked, recipientDisabled } =
    useRecipientsChecked(openState)

  const isRecipientChecked = recipientChecked(recipient)

  const keyGenerator = (type: string) => `item-${type}-${recipient.nameId}`

  const isMeters = filters.filterType === FilterTypeEnum.Objects
  const isObjects = filters.filterType === FilterTypeEnum.Meters
  const isBoardAndCommitee = filters.filterType === FilterTypeEnum.BoardMembers
  const isOtherOrBoardContacts =
    filters.filterType === FilterTypeEnum.BoardContacts ||
    filters.filterType === FilterTypeEnum.OtherContacts
  // Check if addresses contains , in string
  const hasMultipleAddresses = recipient.address.includes(',')

  const CheckboxComponent = (
    <Checkbox
      key={keyGenerator('checkbox')}
      checked={isRecipientChecked}
      onChange={handleSingleCheckboxChange(recipient)}
      disabled={recipientDisabled(recipient)}
    />
  )

  const NameComponent = (
    <RowCell key={keyGenerator('name')}>
      <Text wordBreak="break-all" small bold={isMobile}>
        {recipient.name}
      </Text>
    </RowCell>
  )

  const PhoneComponent = (
    <RowCell key={keyGenerator('mobile')}>
      <Text wordBreak="break-all" small>
        {getFormattedPhone(recipient.phonenumber)}
      </Text>
    </RowCell>
  )

  const EmailComponent = (
    <RowCell title={recipient.email} key={keyGenerator('email')}>
      <Text wordBreak="break-all" small>
        {recipient.email}
      </Text>
    </RowCell>
  )

  const ContactComponent =
    openState === 'Message'
      ? null
      : openState === 'SMS'
        ? PhoneComponent
        : EmailComponent

  const UnitPropertyComponent = (
    <RowCell key={keyGenerator('unit-property')}>
      <Text small>{recipient.unitPropertyNo}</Text>
    </RowCell>
  )

  const BoardFunctionComponent = (
    <RowCell key={keyGenerator('member-function')}>
      <Text small>{recipient?.memberFunction || ''}</Text>
    </RowCell>
  )

  const PositionComponent = (
    <RowCell key={keyGenerator('position')}>
      <Text small>{recipient?.position || ''}</Text>
    </RowCell>
  )

  const CompanyNameComponent = (
    <RowCell key={keyGenerator('company-name')}>
      <Text small>{recipient?.companyName || ''}</Text>
    </RowCell>
  )

  const SsbnrComponent = (
    <RowCell key={keyGenerator('ssbnr')} ellipsis>
      <Text small>{recipient?.ssbNo || ''}</Text>
    </RowCell>
  )

  const AddressComponent = (
    <RowCell ellipsis title={recipient.address} key={keyGenerator('address')}>
      {hasMultipleAddresses ? (
        <>
          <span data-tip data-for={'address-tooltip-' + recipient.nameId}>
            <Text small>{recipient.address}</Text>
          </span>
          <ReactTooltip
            place="bottom"
            type="info"
            id={'address-tooltip-' + recipient.nameId}
            effect="solid"
            backgroundColor={bgLight}
          >
            <Ul>
              {recipient.address.split(',').map((address, i) => (
                <Li key={i}>
                  <Text small>{address}</Text>
                </Li>
              ))}
            </Ul>
          </ReactTooltip>
        </>
      ) : (
        <Text small>{recipient.address}</Text>
      )}
    </RowCell>
  )

  const ObjectAndMeterComponent = (
    <RowCell key={keyGenerator('object-and-meter')}>
      {recipient.objectOrMeter?.length ? (
        <>
          <span
            data-tip
            data-for={'object-or-meter-tooltip-' + recipient.nameId}
          >
            <Text small>
              {recipient.objectOrMeter.map((o) => o.name).join(', ') || ''}
            </Text>
          </span>

          {recipient.objectOrMeter.length > 1 ? (
            <ReactTooltip
              place="bottom"
              type="info"
              id={'object-or-meter-tooltip-' + recipient.nameId}
              effect="solid"
              backgroundColor={bgLight}
            >
              <Ul>
                {recipient.objectOrMeter.map((o, i) => (
                  <Li key={i + o.id}>
                    <Text small>{o.name}</Text>
                  </Li>
                ))}
              </Ul>
            </ReactTooltip>
          ) : null}
        </>
      ) : null}
    </RowCell>
  )

  const ReservedComponent =
    isMobile &&
    !recipient.reservedAgainstElectronicCommunication &&
    recipient.hasSignedIn ? null : (
      <RowCell
        key={keyGenerator('reserved')}
        title={
          !recipient.hasSignedIn
            ? `Personen har ikke logget inn i ${myPageShort} og kan ikke motta melding`
            : 'Personen har reservert seg mot elektronisk kommunikasjon'
        }
      >
        {openState === 'Message' && !recipient.hasSignedIn ? (
          <IconWrapper>
            <IconWarning color={alert} />
            {isMobile && <Text small>Har ikke logget inn i {myPageShort}</Text>}
          </IconWrapper>
        ) : recipient.reservedAgainstElectronicCommunication ? (
          <IconWrapper>
            <IconStop colorVariant="info" />
            {isMobile && (
              <Text small>Reservert mot elektronisk kommunikasjon</Text>
            )}
          </IconWrapper>
        ) : null}
      </RowCell>
    )

  const getComponentList = () => {
    if (isBoardAndCommitee)
      return [
        CheckboxComponent,
        NameComponent,
        ContactComponent,
        BoardFunctionComponent,
      ]

    if (isMeters || isObjects)
      return [
        CheckboxComponent,
        NameComponent,
        ObjectAndMeterComponent,
        ContactComponent,
        ReservedComponent,
      ]

    if (isOtherOrBoardContacts)
      return [
        CheckboxComponent,
        NameComponent,
        CompanyNameComponent,
        PositionComponent,
        ContactComponent,
      ]

    return [
      CheckboxComponent,
      NameComponent,
      ContactComponent,
      UnitPropertyComponent,
      AddressComponent,
      SsbnrComponent,
      ReservedComponent,
    ]
  }

  return <TableRow>{getComponentList()}</TableRow>
}

export default RecipientListItem
